import { FC, useState } from 'react';
import { Hidden } from 'react-grid-system';

import { background, BackgroundProps } from 'styled-system';

import useTranslations from 'modules/I18n/hooks/useTranslations';
import useModalFlags, { ModalFlag } from 'modules/Modals/hooks/useModalFlags';
import useTheme from 'modules/Theme/hooks/useTheme';
import styled from 'modules/Theme/styled-components';

import Box from '../Box';
import FadeOutContainer from '../FadeOutContainer';
import { OnlyIconButton } from '../Form';
import { IconArrowSecondary, IconCross } from '../Icons';
import Link from '../Link';
import { LinkProps } from '../Link/Link';
import Text from '../Text';
import { texts } from './messages';

interface HorizontalBannerProps extends LinkProps, BackgroundProps {
  backgroundBanner?: string;
  cta: string;
  image?: string;
  imageUrl?: string;
  initialHeight?: string;
  isCloseable?: boolean;
  text: string;
  textColor?: string;
  title: string;
  to: string;
}

const StyledBanner = styled(Box)`
   ${background}
   .horizontalBanner__link {
      @media (hover: hover) {
        &:hover {
          .horizontalBanner__text--cta {
            text-decoration: underline;
          }
        }
      }

      &:focus {
        .horizontalBanner__text--cta {
          text-decoration: underline;
        }
      }
    }
`;

export const HorizontalBanner: FC<HorizontalBannerProps> = ({
  backgroundBanner,
  cta,
  image,
  imageUrl,
  initialHeight = '110px',
  isCloseable,
  text,
  textColor,
  title,
  to,
}) => {
  const { t } = useTranslations();
  const theme = useTheme();
  const { setFlag, getFlag } = useModalFlags();
  const hiddenBanner = getFlag(ModalFlag.HIDE_HORIZONTAL_BANNER);
  const [closingBanner, setClosingBanner] = useState(false);
  const handleClick = () => {
    setClosingBanner(true);
    setFlag({
      flag: ModalFlag.HIDE_HORIZONTAL_BANNER,
      status: true,
    });
  };

  if (hiddenBanner) {
    return null;
  }
  if (closingBanner) {
    return (
      <FadeOutContainer
        background={backgroundBanner}
        initialHeight={initialHeight}
      />
    );
  }
  return (
    <StyledBanner
      aria-labelledby="banner-title"
      backgroundColor={backgroundBanner}
      display={isCloseable && 'grid'}
      gridTemplateColumns={isCloseable && '1fr 30px'}
      minHeight={{
        _: isCloseable ? '110px' : '126px',
        sm: '141px',
        lg: '123px',
      }}
      paddingRight={isCloseable ? '16px' : undefined}
      role="complementary"
      tag="section"
    >
      <Link
        ariaLabel={`${title} ${text}`}
        className="horizontalBanner__link"
        color={textColor}
        display="grid"
        gridTemplateColumns={{
          _: isCloseable ? '104px 1fr' : '98px 1fr',
          sm: '235px 1fr',
          md: '388px 1fr',
        }}
        gridTemplateRows={{
          _: isCloseable ? undefined : 'minmax(126px , auto)',
          sm: 'initial',
        }}
        to={to}
      >
        <Box
          backgroundPosition="right center"
          backgroundRepeat="no-repeat"
          backgroundImage={
            imageUrl
              ? `url(${imageUrl})`
              : {
                  _: `url('/assets/banners/horizontal/${image}-mobile.svg')`,
                  sm: `url('/assets/banners/horizontal/${image}-tablet.svg')`,
                  md: `url('/assets/banners/horizontal/${image}-desktop.svg')`,
                  lg: `url('/assets/banners/horizontal/${image}-large.svg')`,
                }
          }
          backgroundSize="cover"
          display="block"
          height="100%"
          tag="span"
          width="100%"
        />
        <Box
          alignSelf={{ _: 'center', sm: 'initial' }}
          padding={{ _: '8px', sm: '16px' }}
          tag="span"
        >
          <Text
            color="inherit"
            display="block"
            fontSize={12}
            fontWeight={600}
            id="banner-title"
            lineHeight={14}
            marginBottom="8px"
            tag="span"
            textTransform="uppercase"
          >
            {title}
          </Text>
          <Text
            color="inherit"
            display="block"
            fontSize={14}
            lineHeight={{ _: 14, sm: 18 }}
            marginBottom={{ sm: '8px' }}
            tag="span"
          >
            {text}
          </Text>
          <Hidden xs>
            <Text
              alignItems="center"
              className="horizontalBanner__text--cta"
              color="inherit"
              columnGap="8px"
              display="flex"
              fontSize={14}
              tag="span"
            >
              {cta}
              <IconArrowSecondary color="inherit" size={18} />
            </Text>
          </Hidden>
        </Box>
      </Link>
      {isCloseable && (
        <Box tag="span" alignSelf="start" marginTop={{ _: '8px', sm: '16px' }}>
          <OnlyIconButton
            aria-label={t(texts.close)}
            icon={<IconCross color="primary300" />}
            iconHoverColor={theme.colors.primary400}
            onClick={handleClick}
            size={30}
            variant="gray"
          />
        </Box>
      )}
    </StyledBanner>
  );
};

StyledBanner.defaultProps = {
  marginBottom: { sm: '16px' },
};
